import React from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Autocomplete,
  Switch,
  FormControlLabel,
  Typography,
  Box
} from '@mui/material';

const FormField = ({
  type = 'text',
  name,
  label,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  required = false,
  options = [],
  multiple = false,
  disabled = false,
  placeholder,
  rows,
  allowClear = true, // New prop to control clearing behavior
  children,
  ...props
}) => {
  const hasError = Boolean(error);

  // Prevent duplicate asterisks when both label has an asterisk and required={true} is passed
  const cleanLabel = (typeof label === 'string' && required)
    ? label.replace(/\s*\*+\s*$/, '').trim()
    : label;

  const handleBlur = () => {
    if (onBlur) {
      onBlur(name);
    }
  };

  const handleChange = (newValue) => {
    if (!onChange) return;

    const syntheticEvent = {
      target: { name: name || '', value: newValue, checked: Boolean(newValue) },
      currentTarget: { name: name || '', value: newValue, checked: Boolean(newValue) },
      name: name || '',
      value: newValue,
      checked: Boolean(newValue),
      toString: () => name || '',
      valueOf: () => name || '',
      [Symbol.toPrimitive]: () => name || ''
    };

    // If caller explicitly defined a 1-arg handler (e.g. `(e) => setVal(e.target.value)`):
    if (typeof onChange === 'function' && onChange.length === 1) {
      onChange(syntheticEvent);
      return;
    }

    // Standard FormField contract across FluxERP:
    // Arg 1: field name (or syntheticEvent if name is not passed)
    // Arg 2: raw value
    // Arg 3: syntheticEvent
    if (name) {
      onChange(name, newValue, syntheticEvent);
    } else {
      onChange(syntheticEvent, newValue, name);
    }
  };

  switch (type) {
    case 'select': {
      const safeSelectOptions = Array.isArray(options) ? options : [];
      const hasEmptyOption = safeSelectOptions.some(o => o.value === '' || o.value === null || o.value === undefined);
      const showNoneOption = !multiple && allowClear && !required && !hasEmptyOption;
      const selectLabelId = name ? `${name}-select-label` : undefined;
      const selectValue = multiple
        ? (Array.isArray(value) ? value : (value ? [value] : []))
        : (value ?? '');

      return (
        <FormControl fullWidth error={hasError} disabled={disabled}>
          <InputLabel id={selectLabelId} required={required}>{cleanLabel}</InputLabel>
          <Select
            labelId={selectLabelId}
            label={cleanLabel}
            multiple={multiple}
            {...props}
            name={name}
            value={selectValue}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
          >
            {children
              ? children
              : [
                  showNoneOption ? (
                    <MenuItem key="__none__" value="">
                      <em>None</em>
                    </MenuItem>
                  ) : null,
                  ...safeSelectOptions.map((option, idx) => {
                    const optValue = (typeof option === 'object' && option !== null) ? option.value : option;
                    const optLabel = (typeof option === 'object' && option !== null) ? (option.label ?? option.name ?? optValue) : option;
                    return (
                      <MenuItem
                        key={optValue !== undefined && optValue !== null && optValue !== '' ? String(optValue) : `empty-opt-${idx}`}
                        value={optValue}
                      >
                        {optLabel}
                      </MenuItem>
                    );
                  })
                ].filter(Boolean)}
          </Select>
          {(hasError || helperText) && (
            <FormHelperText>{error || helperText}</FormHelperText>
          )}
        </FormControl>
      );
    }

    case 'autocomplete': {
      const safeOptions = Array.isArray(options) ? options : [];
      // Destructure props that must NOT be forwarded to TextField.
      // eslint-disable-next-line no-unused-vars
      const { inputProps: callerInputProps, options: _opts, multiple: _multi, allowClear: _ac, ...textFieldRestProps } = props;
      return (
        <Autocomplete
          options={safeOptions}
          value={safeOptions.find(opt => opt.value === value) || null}
          onChange={(event, newValue) => {
            const rawVal = newValue && typeof newValue === 'object' && 'value' in newValue ? newValue.value : (newValue ?? '');
            handleChange(rawVal);
          }}
          onBlur={handleBlur}
          getOptionLabel={(option) => (typeof option === 'object' ? option.label : '') || ''}
          isOptionEqualToValue={(option, val) => option?.value === val?.value}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              fullWidth
              {...params}
              label={cleanLabel}
              required={required}
              error={hasError}
              helperText={error || helperText}
              placeholder={placeholder}
              inputProps={{ ...params.inputProps, ...(callerInputProps || {}) }}
              {...textFieldRestProps}
            />
          )}
        />
      );
    }

    case 'switch': {
      return (
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(value)}
                onChange={(e) => handleChange(e.target.checked)}
                onBlur={handleBlur}
                disabled={disabled}
                {...props}
              />
            }
            label={cleanLabel}
          />
          {(hasError || helperText) && (
            <Typography variant="caption" color={hasError ? 'error' : 'text.secondary'} display="block">
              {error || helperText}
            </Typography>
          )}
        </Box>
      );
    }

    case 'textarea': {
      return (
        <TextField
          fullWidth
          multiline
          rows={rows || 4}
          label={cleanLabel}
          value={value ?? ''}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          error={hasError}
          helperText={error || helperText}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          {...props}
        />
      );
    }

    default: {
      return (
        <TextField
          fullWidth
          type={type}
          label={cleanLabel}
          value={value ?? ''}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          error={hasError}
          helperText={error || helperText}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          multiline={type === 'textarea'}
          rows={type === 'textarea' ? rows || 4 : undefined}
          InputLabelProps={{
            shrink: (['date', 'datetime-local', 'time'].includes(type) || Boolean(placeholder) || (value !== undefined && value !== null && value !== '')) ? true : undefined
          }}
          {...props}
        />
      );
    }
  }
};

export default FormField;
